import React from 'react';
import { BaseContainerEffect, ContainerEffectConfig, LayerType } from './BaseContainerEffect';
import { EasingFunction, Easing } from './BaseEffect';

/**
 * Configuration interface specific to blur effects
 */
export interface ContainerBlurEffectConfig {
    targetLayers: LayerType[];
    excludeLayers?: LayerType[];
    blurFrom: number;        // Starting blur amount in pixels
    blurTo: number;          // Ending blur amount in pixels
    duration: number;
    at: number;
    easing?: EasingFunction;
}

/**
 * ContainerBlurEffect - Container-based blur effect
 * 
 * Groups specified layers into a single container and applies a unified
 * blur effect to the entire container. This creates more natural blur
 * behavior compared to per-layer blur effects.
 * 
 * Benefits of container-based blur:
 * - Unified blur creates natural depth-of-field effect
 * - Better performance with single filter operation
 * - Maintains visual cohesion between layers
 * - Simplified GPU processing
 * 
 * Use cases:
 * - Background blur while keeping UI sharp
 * - Scene transition blur effects
 * - Focus effects that blur everything except specific elements
 */
export class ContainerBlurEffect extends BaseContainerEffect {
    constructor(config: ContainerBlurEffectConfig) {
        super({
            targetLayers: config.targetLayers,
            excludeLayers: config.excludeLayers,
            from: config.blurFrom,
            to: config.blurTo,
            duration: config.duration,
            at: config.at,
            easing: config.easing
        });
    }

    getContainerStyle(progress: number): React.CSSProperties {
        const currentBlur = this.from + (this.to - this.from) * progress;
        
        return {
            filter: `blur(${currentBlur}px)`
        };
    }

    getEffectType(): string {
        return 'container-blur';
    }

    // Static factory methods for common blur patterns

    /**
     * Create blur effect targeting only background layers
     * Perfect for subtle background blur while keeping content sharp
     */
    static createBackgroundBlur(
        blurAmount: number, 
        duration: number, 
        at: number,
        easing?: EasingFunction
    ): ContainerBlurEffect {
        return new ContainerBlurEffect({
            targetLayers: ['background'],
            blurFrom: 0,
            blurTo: blurAmount,
            duration,
            at,
            easing
        });
    }

    /**
     * Create blur effect targeting background and scene layers
     * Good for blurring main content while keeping UI elements sharp
     */
    static createContentBlur(
        blurAmount: number, 
        duration: number, 
        at: number,
        easing?: EasingFunction
    ): ContainerBlurEffect {
        return new ContainerBlurEffect({
            targetLayers: ['background', 'scene'],
            excludeLayers: ['textOverlay', 'caption', 'branding'],
            blurFrom: 0,
            blurTo: blurAmount,
            duration,
            at,
            easing
        });
    }

    /**
     * Create blur effect targeting background, scene, and overlay layers
     * Excludes text and UI elements for readability
     */
    static createMainContentBlur(
        blurAmount: number, 
        duration: number, 
        at: number,
        easing?: EasingFunction
    ): ContainerBlurEffect {
        return new ContainerBlurEffect({
            targetLayers: ['background', 'scene', 'overlay'],
            excludeLayers: ['textOverlay', 'caption', 'branding'],
            blurFrom: 0,
            blurTo: blurAmount,
            duration,
            at,
            easing
        });
    }

    /**
     * Create blur effect targeting all content except captions
     * Blurs text overlays but keeps captions readable
     */
    static createExcludingCaptions(
        blurAmount: number, 
        duration: number, 
        at: number,
        easing?: EasingFunction
    ): ContainerBlurEffect {
        return new ContainerBlurEffect({
            targetLayers: ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay'],
            excludeLayers: ['caption', 'branding'],
            blurFrom: 0,
            blurTo: blurAmount,
            duration,
            at,
            easing
        });
    }

    /**
     * Create blur effect targeting all content except branding
     * Maximum blur while preserving brand elements
     */
    static createExceptBranding(
        blurAmount: number, 
        duration: number, 
        at: number,
        easing?: EasingFunction
    ): ContainerBlurEffect {
        return new ContainerBlurEffect({
            targetLayers: ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay', 'caption'],
            excludeLayers: ['branding'],
            blurFrom: 0,
            blurTo: blurAmount,
            duration,
            at,
            easing
        });
    }

    /**
     * Create subtle blur effect perfect for scene transitions
     * Quick blur in/out with moderate intensity
     */
    static createSceneTransition(
        duration: number,
        at: number,
        maxBlur: number = 10,
        easing?: EasingFunction
    ): ContainerBlurEffect {
        return new ContainerBlurEffect({
            targetLayers: ['background', 'scene', 'overlay', 'overlayTransition'],
            excludeLayers: ['textOverlay', 'caption', 'branding'],
            blurFrom: 0,
            blurTo: maxBlur,
            duration,
            at,
            easing: easing || Easing.easeInOutCubic
        });
    }

    /**
     * Create custom blur effect with full control over layers
     */
    static createCustom(config: ContainerBlurEffectConfig): ContainerBlurEffect {
        return new ContainerBlurEffect(config);
    }
}